import { useEffect, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  FileText as PageIcon,
  Layers as TabIcon,
  Paperclip,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { api, type AttachmentDto } from '../lib/api';
import { useVault } from '../../../core/lib/vault';
import { useCanEdit } from '../lib/usePermissions';
import { decryptJson } from '../../../core/crypto';
import {
  CATEGORY_ORDER,
  categoryFor,
  formatSize,
  iconFor,
  type AttachmentCategory,
} from '../lib/attachmentTypes';
import {
  ATTACHMENTS_CHANGED_EVENT,
  uploadFile,
  uploadFiles,
  dragHasFiles,
  type AttachmentsChangedDetail,
} from '../lib/attachmentUpload';
import {
  acquireAttachmentUrl,
  releaseAttachmentUrl,
} from '../lib/attachmentBlobCache';
import {
  buildReferenceMap,
  type DocRef,
} from '../lib/attachmentReferences';
import { openAttachmentPreview } from '../lib/attachmentPreview';

interface Row {
  id: string;
  filename: string;
  mimetype: string;
  category: AttachmentCategory;
  size: number;
  createdAt: string;
}

type SortField = 'name' | 'type' | 'size' | 'date';

interface Props {
  customerId: string;
  customerName: string;
  onClose: () => void;
  onNavigateToDoc: (pageId: string, tabId: string | null) => void;
}

export function AttachmentsView({
  customerId,
  customerName,
  onClose,
  onNavigateToDoc,
}: Props) {
  const vaultKey = useVault((s) => s.vaultKey);
  // Upload + delete are edit-tier operations; viewers see the gallery without
  // the Upload button or per-row Trash icons. Server enforces the same check.
  const canEdit = useCanEdit();
  const [rows, setRows] = useState<Row[]>([]);
  const [refMap, setRefMap] = useState<Map<string, DocRef[]>>(new Map());
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function reload() {
    if (!vaultKey || !customerId) return;
    setLoading(true);
    setError(null);
    try {
      const [list, refs] = await Promise.all([
        api.listAttachments(customerId),
        buildReferenceMap(customerId, vaultKey),
      ]);
      const decrypted = await Promise.all(
        list.attachments.map((a) => decryptRow(a, vaultKey)),
      );
      setRows(decrypted.filter((x): x is Row => x !== null));
      setRefMap(refs);
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Could not load attachments.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, vaultKey]);

  useEffect(() => {
    if (!customerId || !vaultKey) return;
    let pending = false;
    function onChanged(e: Event) {
      const d = (e as CustomEvent<AttachmentsChangedDetail>).detail;
      if (!d || d.customerId !== customerId) return;
      if (pending) return;
      pending = true;
      queueMicrotask(() => {
        pending = false;
        void reload();
      });
    }
    window.addEventListener(ATTACHMENTS_CHANGED_EVENT, onChanged as EventListener);
    return () =>
      window.removeEventListener(ATTACHMENTS_CHANGED_EVENT, onChanged as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, vaultKey]);

  async function handleUploadInput(files: FileList | null) {
    if (!vaultKey || !customerId || !files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const f of Array.from(files)) {
        await uploadFile(f, customerId, vaultKey);
      }
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Upload failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.deleteAttachment(id);
      window.dispatchEvent(
        new CustomEvent<AttachmentsChangedDetail>(ATTACHMENTS_CHANGED_EVENT, {
          detail: { customerId, attachmentId: id },
        }),
      );
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (!vaultKey || !customerId) return;
    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await uploadFiles(e.dataTransfer.files, customerId, vaultKey);
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Upload failed.');
    } finally {
      setBusy(false);
    }
  }

  const images = rows.filter((r) => r.category === 'image');
  const imagesSorted = [...images].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const files = rows.filter((r) => r.category !== 'image');
  const filesSorted = [...files].sort((a, b) => compareRows(a, b, sortField, sortDir));

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'date' || field === 'size' ? 'desc' : 'asc');
    }
  }

  return (
    <section
      className="flex-1 min-w-0 flex flex-col bg-ink-950"
      onDragOver={(e) => {
        if (!dragHasFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setDragOver(true);
      }}
      onDragLeave={(e) => {
        if ((e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) return;
        setDragOver(false);
      }}
      onDrop={handleDrop}
    >
      <div className="flex items-center gap-2 border-b border-ink-800 bg-ink-900 px-4 py-2.5">
        <Paperclip size={16} className="text-dragon-500" />
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold truncate">Attachments</h2>
          <p className="text-xs text-ink-500 truncate">
            {customerName || 'Customer'} · {rows.length} item{rows.length === 1 ? '' : 's'}
          </p>
        </div>
        {canEdit && (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="btn-primary !px-2 !py-1"
              title="Upload"
            >
              <Upload size={14} className="mr-1" />
              Upload
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                void handleUploadInput(e.target.files);
                e.currentTarget.value = '';
              }}
            />
          </>
        )}
        <button onClick={onClose} className="btn-ghost !p-1" title="Close">
          <X size={16} />
        </button>
      </div>

      {(busy || error || loading) && (
        <div className="px-4 py-1.5 text-xs border-b border-ink-800 bg-ink-900/60">
          {busy && <span className="text-ink-300">Uploading…</span>}
          {loading && !busy && <span className="text-ink-400">Loading…</span>}
          {error && <span className="text-red-400">{error}</span>}
        </div>
      )}

      <div
        className={`flex-1 min-h-0 overflow-y-auto relative ${
          dragOver ? 'bg-dragon-500/5' : ''
        }`}
      >
        {rows.length === 0 && !loading && (
          <div className="p-8 text-sm text-ink-500">
            No attachments for this customer yet. Drop files here or click Upload.
          </div>
        )}

        {imagesSorted.length > 0 && (
          <div className="px-4 pt-4 pb-2">
            <h3 className="text-xs uppercase tracking-wider text-ink-400 mb-2">
              Images ({imagesSorted.length})
            </h3>
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
            >
              {imagesSorted.map((r) => (
                <ImageTile
                  key={r.id}
                  row={r}
                  refs={refMap.get(r.id) ?? []}
                  onPreview={() => openAttachmentPreview(r.id)}
                  onNavigate={onNavigateToDoc}
                  onDelete={() => void handleDelete(r.id)}
                  busy={busy}
                />
              ))}
            </div>
          </div>
        )}

        {filesSorted.length > 0 && (
          <div className="px-4 pt-4 pb-6">
            <h3 className="text-xs uppercase tracking-wider text-ink-400 mb-2">
              Files ({filesSorted.length})
            </h3>
            <div className="overflow-x-auto rounded border border-ink-800">
              <table className="w-full text-sm">
                <thead className="bg-ink-900 text-ink-400 text-xs uppercase tracking-wider">
                  <tr>
                    <SortHeader
                      field="name"
                      label="Name"
                      sortField={sortField}
                      sortDir={sortDir}
                      onClick={toggleSort}
                    />
                    <SortHeader
                      field="type"
                      label="Type"
                      sortField={sortField}
                      sortDir={sortDir}
                      onClick={toggleSort}
                    />
                    <SortHeader
                      field="size"
                      label="Size"
                      sortField={sortField}
                      sortDir={sortDir}
                      onClick={toggleSort}
                    />
                    <SortHeader
                      field="date"
                      label="Added"
                      sortField={sortField}
                      sortDir={sortDir}
                      onClick={toggleSort}
                    />
                    <th className="text-left font-normal px-3 py-2">Used in</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {filesSorted.map((r) => {
                    const Icon = iconFor(r.category);
                    return (
                      <tr
                        key={r.id}
                        className="border-t border-ink-800 hover:bg-ink-900/60 cursor-pointer"
                        onClick={() => openAttachmentPreview(r.id)}
                      >
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon size={14} className="text-dragon-500 flex-shrink-0" />
                            <span className="truncate">{r.filename}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-ink-400">{r.category}</td>
                        <td className="px-3 py-2 text-ink-400 whitespace-nowrap">
                          {formatSize(r.size)}
                        </td>
                        <td className="px-3 py-2 text-ink-400 whitespace-nowrap">
                          {new Date(r.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2">
                          <UsedInCell
                            refs={refMap.get(r.id) ?? []}
                            onNavigate={onNavigateToDoc}
                          />
                        </td>
                        <td className="px-1 py-2">
                          {canEdit && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDelete(r.id);
                              }}
                              disabled={busy}
                              className="btn-ghost !p-1 text-ink-500 hover:text-red-400"
                              title="Move to Orphanage"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {dragOver && (
          <div className="pointer-events-none absolute inset-2 rounded-lg border-2 border-dashed border-dragon-500/70 flex items-center justify-center bg-dragon-500/5 text-dragon-500 text-sm font-medium">
            Drop to upload
          </div>
        )}
      </div>
    </section>
  );
}

function SortHeader({
  field,
  label,
  sortField,
  sortDir,
  onClick,
}: {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDir: 'asc' | 'desc';
  onClick: (f: SortField) => void;
}) {
  const active = field === sortField;
  return (
    <th
      className={`text-left font-normal px-3 py-2 cursor-pointer select-none ${
        active ? 'text-ink-200' : ''
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onClick(field);
      }}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (sortDir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
      </span>
    </th>
  );
}

function ImageTile({
  row,
  refs,
  onPreview,
  onNavigate,
  onDelete,
  busy,
}: {
  row: Row;
  refs: DocRef[];
  onPreview: () => void;
  onNavigate: (pageId: string, tabId: string | null) => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const vaultKey = useVault((s) => s.vaultKey);
  const canEdit = useCanEdit();
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!vaultKey) return;
    (async () => {
      try {
        const r = await acquireAttachmentUrl(row.id, vaultKey);
        if (cancelled) {
          releaseAttachmentUrl(row.id);
          return;
        }
        setUrl(r.url);
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      releaseAttachmentUrl(row.id);
    };
  }, [row.id, vaultKey]);

  return (
    <div className="group rounded overflow-hidden border border-ink-800 bg-ink-900 hover:border-ink-700 transition-colors flex flex-col">
      <div className="relative">
        <button
          onClick={onPreview}
          className="block w-full aspect-square bg-ink-950 overflow-hidden"
          title={`Open ${row.filename}`}
        >
          {url ? (
            <img
              src={url}
              alt={row.filename}
              className="w-full h-full object-cover"
              draggable={false}
            />
          ) : err ? (
            <div className="w-full h-full flex items-center justify-center text-ink-500 text-xs">
              Failed to load
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-ink-500 text-xs">
              Decrypting…
            </div>
          )}
        </button>
        {canEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            disabled={busy}
            className="absolute top-1 right-1 p-1 rounded bg-ink-950/70 border border-ink-700 text-ink-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Move to Orphanage"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
      <div className="px-2 py-1.5 flex flex-col gap-1 min-w-0">
        <div className="text-xs truncate" title={row.filename}>
          {row.filename}
        </div>
        <div className="text-[10px] text-ink-500 flex items-center justify-between gap-2">
          <span>{formatSize(row.size)}</span>
          <span>{new Date(row.createdAt).toLocaleDateString()}</span>
        </div>
        {refs.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-0.5">
            {refs.slice(0, 3).map((r, idx) => (
              <RefChip key={`${r.docId}-${idx}`} docRef={r} onNavigate={onNavigate} />
            ))}
            {refs.length > 3 && (
              <span className="text-[10px] text-ink-500 px-1">+{refs.length - 3}</span>
            )}
          </div>
        )}
        {refs.length === 0 && (
          <span className="text-[10px] text-ink-600 italic">Unreferenced</span>
        )}
      </div>
    </div>
  );
}

function UsedInCell({
  refs,
  onNavigate,
}: {
  refs: DocRef[];
  onNavigate: (pageId: string, tabId: string | null) => void;
}) {
  if (refs.length === 0) {
    return <span className="text-xs text-ink-600 italic">Unreferenced</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {refs.map((r, idx) => (
        <RefChip key={`${r.docId}-${idx}`} docRef={r} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

function RefChip({
  docRef,
  onNavigate,
}: {
  docRef: DocRef;
  onNavigate: (pageId: string, tabId: string | null) => void;
}) {
  const isTab = docRef.parentId != null;
  const label = isTab
    ? `${docRef.parentTitle ?? '(untitled)'} / ${docRef.title}`
    : docRef.title;
  const Icon = isTab ? TabIcon : PageIcon;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (isTab) {
          onNavigate(docRef.parentId!, docRef.docId);
        } else {
          onNavigate(docRef.docId, null);
        }
      }}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-ink-800 hover:bg-ink-700 border border-ink-700 text-[11px] max-w-[240px]"
      title={label}
    >
      <Icon size={10} className="text-dragon-500 flex-shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
}

async function decryptRow(a: AttachmentDto, key: CryptoKey): Promise<Row | null> {
  try {
    const meta = await decryptJson<{ filename?: string; mimetype?: string }>(
      a.metadata,
      key,
    );
    const filename = meta.filename || `attachment-${a.id}`;
    const mimetype = meta.mimetype || 'application/octet-stream';
    return {
      id: a.id,
      filename,
      mimetype,
      category: categoryFor(mimetype, filename),
      size: a.size,
      createdAt: a.createdAt,
    };
  } catch {
    return null;
  }
}

function compareRows(a: Row, b: Row, field: SortField, dir: 'asc' | 'desc'): number {
  const mul = dir === 'asc' ? 1 : -1;
  switch (field) {
    case 'name':
      return mul * a.filename.localeCompare(b.filename);
    case 'size':
      return mul * (a.size - b.size);
    case 'type': {
      const ca = CATEGORY_ORDER[a.category] ?? 99;
      const cb = CATEGORY_ORDER[b.category] ?? 99;
      if (ca !== cb) return mul * (ca - cb);
      return mul * a.filename.localeCompare(b.filename);
    }
    case 'date':
    default:
      return mul * a.createdAt.localeCompare(b.createdAt);
  }
}
